<template>
  <v-card ref="scroller" height="100%" flat color="bg1" class="whats-new overflow-y-auto">
    <!-- Page header — sticky, so the subscription control stays reachable at any scroll depth. -->
    <header
      ref="header"
      class="whats-new__header"
      :class="$vuetify.theme.isDark ? 'bg2' : 'white'">
      <div class="whats-new__header-inner ds-content-width--medium">
        <div class="whats-new__header-icon">
          <v-icon color="primary" size="20">mdi-bullhorn-outline</v-icon>
        </div>
        <div class="whats-new__header-text">
          <h1 class="whats-new__title">What's New</h1>
          <p class="whats-new__subtitle">Product updates and release notes</p>
        </div>
        <v-btn text small color="primary" @click="openPreferences">
          <v-icon left small>mdi-email-outline</v-icon>
          Email preferences
        </v-btn>
      </div>
    </header>
    <v-divider></v-divider>

    <div class="ds-content-width--medium whats-new__body">
      <!-- First load -->
      <v-skeleton-loader
        v-if="isInitialLoading"
        type="article@3"
        class="transparent whats-new__skeleton" />

      <!-- Nothing published, or the feature is not switched on for this tenant -->
      <div v-else-if="!releases.length && !loadFailed" class="ds-empty-state">
        <v-icon class="ds-empty-state__icon">mdi-bullhorn-outline</v-icon>
        <div class="ds-empty-state__title">No release notes yet</div>
        <div class="ds-empty-state__description">
          Product updates will show up here as soon as they are published.
        </div>
      </div>

      <!-- The first page failed for a reason other than "nothing to show" -->
      <div v-else-if="!releases.length" class="ds-empty-state">
        <v-icon class="ds-empty-state__icon">mdi-cloud-off-outline</v-icon>
        <div class="ds-empty-state__title">Release notes could not be loaded</div>
        <div class="ds-empty-state__description">Check your connection and try again.</div>
        <v-btn text color="primary" class="ds-empty-state__action" @click="fetchReleases">
          Try again
        </v-btn>
      </div>

      <div v-else class="whats-new__layout">
        <release-version-rail
          v-if="showCompactRail"
          ref="compactRail"
          compact
          :groups="versionGroups"
          :active-anchor="activeAnchor"
          class="whats-new__rail--compact"
          @select="goToVersion" />

        <release-version-rail
          v-else
          :groups="versionGroups"
          :active-anchor="activeAnchor"
          :has-more="hasMore"
          :is-loading-more="isLoadingMore"
          class="whats-new__rail"
          @select="goToVersion" />

        <div class="whats-new__stream">
          <release-stream-entry
            v-for="(release, index) in releases"
            ref="entries"
            :key="anchorFor(release, index)"
            :release="release"
            :index="index"
            :active="anchorFor(release, index) === activeAnchor"
            :is-latest="index === 0"
            :body="bodyFor(release)"
            :is-loading="isBodyLoading(release)"
            @retry="loadBody(release)" />

          <div v-if="hasMore" class="whats-new__more">
            <v-progress-circular
              v-if="isLoadingMore"
              indeterminate
              size="20"
              width="2"
              color="primary" />
            <v-btn v-else text small color="primary" @click="loadMore">
              Show earlier releases
            </v-btn>
          </div>

          <p v-else class="whats-new__end">You have reached the first release.</p>
        </div>
      </div>
    </div>

    <!-- Email preferences -->
    <v-dialog v-model="preferencesDialog" persistent max-width="440">
      <v-card class="ds-dialog" color="bg2">
        <div class="ds-dialog__header">
          <div class="ds-dialog__header-icon">
            <v-icon color="primary" size="20">mdi-email-outline</v-icon>
          </div>
          <div class="ds-dialog__header-text">
            <div class="ds-dialog__title">Email preferences</div>
            <div class="ds-dialog__subtitle">Choose whether we email you about new releases</div>
          </div>
        </div>

        <div class="ds-dialog__body">
          <v-skeleton-loader v-if="subscriptionLoading" type="list-item" class="transparent" />
          <v-switch
            v-else
            v-model="subscribed"
            dense
            inset
            hide-details
            color="primary"
            label="Email me when a new release is published" />
          <div class="ds-dialog__hint mt-3">
            You can always read the latest updates on this page, even with emails turned off.
          </div>
        </div>

        <div class="ds-dialog__footer">
          <v-spacer />
          <v-btn text small :disabled="subscriptionSaving" @click="preferencesDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            small
            depressed
            :loading="subscriptionSaving"
            :disabled="subscriptionLoading"
            @click="savePreferences">
            Save
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
  import { $formatDate } from '@Utils';
  import { error } from '@/utils/debugLogger';
  import ReleaseStreamEntry from '@ReleaseNotes/components/tenant/ReleaseStreamEntry.vue';
  import ReleaseVersionRail from '@ReleaseNotes/components/tenant/ReleaseVersionRail.vue';
  import {
    releaseAnchorId,
    releaseName,
    releasePublishedAt,
    releaseSlug,
    releaseYear,
    unwrapReleaseList,
  } from '@ReleaseNotes/utils/releaseFields';

  const SUBSCRIBED_KEYS = ['isSubscribed', 'subscribed', 'emailEnabled'];

  const PAGE_SIZE = 20;
  // Distance from the container top at which a release counts as "the one being read". Roughly
  // one header's worth, so the rail flips as a title reaches the top of the reading area.
  const SPY_OFFSET_PX = 140;
  // How close to the bottom the reader gets before the next page is fetched.
  const INFINITE_SCROLL_MARGIN_PX = 800;
  // How far ahead of the viewport a release body is fetched.
  const BODY_PRELOAD_MARGIN_PX = 600;
  // A deep-linked version may sit on a later page. Page towards it, but never indefinitely.
  const MAX_DEEP_LINK_PAGES = 5;
  // Breathing room between the sticky header and the entry a jump lands on.
  const JUMP_GAP_PX = 16;

  /**
   * Reduces a version label to its numeric core so labels spelled differently still match:
   * the app badge says `v1.1.154`, while a release DTO may carry `1.1.154` or `Release 1.1.154`
   * (`releaseName` probes `name`/`title`/`version`, none of them normalised). Returns '' for a
   * label with no version in it, which never matches anything.
   * @param {*} value
   * @returns {string}
   */
  function versionKey(value) {
    const match = String(value || '').match(/\d+(?:\.\d+)+/);
    return match ? match[0] : '';
  }
  // Matches `--whats-new-header-height`. Only used if the header cannot be measured — the live
  // measurement is what keeps the jump correct when the header wraps at narrow widths.
  const HEADER_HEIGHT_FALLBACK_PX = 73;

  export default {
    name: 'WhatsNew',
    components: { ReleaseStreamEntry, ReleaseVersionRail },
    data() {
      return {
        releases: [],
        total: 0,
        page: 1,
        loadFailed: false,
        isLoadingMore: false,
        activeAnchor: '',
        preferencesDialog: false,
        subscribed: false,
        subscriptionKey: SUBSCRIBED_KEYS[0],
        subscriptionLoading: false,
        subscriptionSaving: false,
      };
    },
    computed: {
      isLoading() {
        return this.$store.getters['RELEASE_NOTES/IS_TENANT_LOADING'];
      },
      bodies() {
        return this.$store.getters['RELEASE_NOTES/GET_TENANT_RELEASE_BODIES'];
      },
      bodyLoadingSlugs() {
        return this.$store.getters['RELEASE_NOTES/GET_TENANT_BODY_LOADING_SLUGS'];
      },
      isInitialLoading() {
        return this.isLoading && !this.releases.length;
      },
      hasMore() {
        return this.total > this.releases.length;
      },
      showCompactRail() {
        return !this.$vuetify.breakpoint.mdAndUp;
      },
      /**
       * Versions grouped into consecutive runs of the same year, preserving the order the server
       * returned. Grouping consecutively rather than bucketing means the rail can never present
       * an order the stream does not actually have.
       */
      versionGroups() {
        const groups = [];
        this.releases.forEach((release, index) => {
          const year = releaseYear(release);
          const publishedAt = releasePublishedAt(release);
          const entry = {
            anchorId: this.anchorFor(release, index),
            name: releaseName(release),
            // Only the compact selector shows a date — the rail column lists versions alone —
            // and it has no year heading above it, so the year is part of the label.
            fullDate: publishedAt ? $formatDate(publishedAt, 'DD MMM YYYY') : '',
          };
          const current = groups[groups.length - 1];
          if (current && current.year === year) current.versions.push(entry);
          else groups.push({ year, versions: [entry] });
        });
        return groups;
      },
    },
    mounted() {
      this.fetchReleases().then(() => {
        this.attachScrollListener();
        this.observeEntries();
        this.openDeepLink();
      });
    },
    beforeDestroy() {
      this.detachScrollListener();
      if (this.bodyObserver) this.bodyObserver.disconnect();
      if (this.spyFrame) window.cancelAnimationFrame(this.spyFrame);
    },
    methods: {
      // -------------------------------------------------------------------- data
      anchorFor(release, index) {
        return releaseAnchorId(release, index);
      },

      bodyFor(release) {
        const slug = releaseSlug(release);
        return (slug && this.bodies[slug]) || null;
      },

      isBodyLoading(release) {
        const slug = releaseSlug(release);
        return !!slug && this.bodyLoadingSlugs.includes(slug);
      },

      fetchReleases() {
        this.loadFailed = false;
        return this.$store
          .dispatch('RELEASE_NOTES/FETCH_TENANT_RELEASES', {
            page: this.page,
            itemsPerPage: PAGE_SIZE,
          })
          .then((data) => {
            const { items, total } = unwrapReleaseList(data);
            this.releases = this.page > 1 ? this.releases.concat(items) : items;
            // A bare-array response has no server total; fall back to what we hold so the page
            // does not keep asking for releases that are not there.
            this.total = Math.max(total, this.releases.length);
            // Newly rendered entries need their own body observers.
            this.$nextTick(this.observeEntries);
          })
          .catch(() => {
            // The action already toasted anything worth toasting; the page renders its own
            // inline state. Rewind the page counter so "Try again" does not skip a page.
            if (this.page > 1) this.page -= 1;
            this.loadFailed = true;
          });
      },

      loadMore() {
        if (this.isLoadingMore || !this.hasMore) return Promise.resolve();
        this.isLoadingMore = true;
        this.page += 1;
        return this.fetchReleases().finally(() => {
          this.isLoadingMore = false;
        });
      },

      /**
       * Fetches one release body. Safe to call repeatedly — the store caches by slug and drops
       * duplicate in-flight requests, so the lazy observer can fire freely.
       * @param {Object} release list DTO
       */
      loadBody(release) {
        const slug = releaseSlug(release);
        if (!slug) return;
        this.$store.dispatch('RELEASE_NOTES/FETCH_TENANT_RELEASE_BODY', slug);
      },

      // ------------------------------------------------------------------ scroll
      /**
       * The page's scroll happens inside the root `v-card` (`overflow-y-auto`), never on the
       * window — a listener on `window` would never fire here.
       * @returns {HTMLElement|null}
       */
      scroller() {
        const card = this.$refs.scroller;
        return card ? card.$el : null;
      },

      entryElements() {
        const entries = this.$refs.entries || [];
        return entries.map((entry) => entry.$el).filter(Boolean);
      },

      /**
       * Total height of the chrome stuck to the top of the scroller: the page header always, plus
       * the compact version selector below `md`, which sticks directly beneath it. Both are
       * `position: sticky` *inside* the scroller, so together they cover the first rows of
       * whatever is scrolled to the top — anything landing there has to be pushed down by this
       * much or it reads as missing. Measured rather than derived from
       * `--whats-new-header-height` so a header that wraps at narrow widths stays correct.
       * @returns {number}
       */
      headerOffset() {
        const header = this.$refs.header;
        const headerHeight = header ? header.getBoundingClientRect().height : 0;

        const compactRail = this.$refs.compactRail;
        const railHeight =
          compactRail && compactRail.$el ? compactRail.$el.getBoundingClientRect().height : 0;

        return (headerHeight || HEADER_HEIGHT_FALLBACK_PX) + railHeight;
      },

      attachScrollListener() {
        const el = this.scroller();
        if (!el) return;
        el.addEventListener('scroll', this.onScroll, { passive: true });
        this.updateActiveAnchor();
      },

      detachScrollListener() {
        const el = this.scroller();
        if (el) el.removeEventListener('scroll', this.onScroll);
      },

      // Scroll-spy and infinite loading share one rAF-throttled handler; two independent
      // listeners on the same container would do the same layout reads twice per frame.
      onScroll() {
        if (this.spyFrame) return;
        this.spyFrame = window.requestAnimationFrame(() => {
          this.spyFrame = null;
          this.updateActiveAnchor();
          this.maybeLoadMore();
        });
      },

      updateActiveAnchor() {
        const el = this.scroller();
        const entries = this.entryElements();
        if (!el || !entries.length) return;

        // The spy line must never sit above where `goToVersion` parks an entry, or jumping to a
        // version would immediately hand the highlight back to the one above it. Below `md` the
        // compact selector adds to the chrome and pushes the landing point past the flat offset.
        const spyLine = Math.max(SPY_OFFSET_PX, this.headerOffset() + JUMP_GAP_PX + 1);
        const top = el.getBoundingClientRect().top + spyLine;
        // The last entry whose heading has passed the spy line is the one being read; before
        // any has, the first release is active.
        let activeIndex = 0;
        entries.forEach((entry, index) => {
          if (entry.getBoundingClientRect().top <= top) activeIndex = index;
        });

        const anchorId = entries[activeIndex].id;
        if (anchorId && anchorId !== this.activeAnchor) this.activeAnchor = anchorId;
      },

      maybeLoadMore() {
        const el = this.scroller();
        if (!el || !this.hasMore || this.isLoadingMore) return;
        const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (remaining <= INFINITE_SCROLL_MARGIN_PX) this.loadMore();
      },

      // -------------------------------------------------------------- lazy bodies
      observeEntries() {
        const root = this.scroller();
        if (!root || typeof window.IntersectionObserver !== 'function') {
          // No observer support: fall back to loading every body that is already rendered.
          this.releases.forEach(this.loadBody);
          return;
        }

        if (!this.bodyObserver) {
          this.bodyObserver = new window.IntersectionObserver(
            (observed) => {
              observed.forEach((record) => {
                if (!record.isIntersecting) return;
                const release = this.releaseByAnchor(record.target.id);
                if (release) this.loadBody(release);
                // A body is fetched once; stop watching so scrolling back up costs nothing.
                this.bodyObserver.unobserve(record.target);
              });
            },
            { root, rootMargin: `${BODY_PRELOAD_MARGIN_PX}px 0px` },
          );
        }

        this.entryElements().forEach((entry) => this.bodyObserver.observe(entry));
      },

      releaseByAnchor(anchorId) {
        return this.releases.find((release, index) => this.anchorFor(release, index) === anchorId);
      },

      /**
       * Anchor of the release carrying `version`, for the `?version=` deep link the app's version
       * badge uses. The badge only knows the running version string — anchors are built from the
       * server's slug — so the two are bridged by matching on the release name.
       * @param {string} version
       * @returns {string} '' when no loaded release matches
       */
      anchorForVersion(version) {
        const wanted = versionKey(version);
        if (!wanted) return '';

        const index = this.releases.findIndex(
          (release) => versionKey(releaseName(release)) === wanted,
        );
        return index === -1 ? '' : this.anchorFor(this.releases[index], index);
      },

      // ------------------------------------------------------------------- links
      /**
       * Scrolls the stream to a version and records it in the URL, so a version can be shared
       * or reloaded without leaving the changelog.
       * @param {string} anchorId
       */
      goToVersion(anchorId) {
        const el = this.scroller();
        const target = anchorId ? document.getElementById(anchorId) : null;
        if (!el || !target) return;

        const reduceMotion =
          window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const offset = target.getBoundingClientRect().top - el.getBoundingClientRect().top;
        // Clear the sticky header, not just a flat 24px: landing the entry at the container top
        // tucked its date and version number underneath the header, leaving the title as the
        // first thing visible.
        const top = el.scrollTop + offset - this.headerOffset() - JUMP_GAP_PX;
        el.scrollTo({
          top: Math.max(0, top),
          behavior: reduceMotion ? 'auto' : 'smooth',
        });

        this.activeAnchor = anchorId;
        // `replaceState` rather than a router push: scrolling within one page should not add a
        // history entry the back button has to walk through.
        window.history.replaceState(null, '', `${this.$route.path}#${anchorId}`);
      },

      /**
       * Honours a `#anchor` fragment or a `?version=` query on load. The target may live on a
       * later page, so pull pages until it appears — bounded, then give up quietly rather than
       * paging forever. Giving up leaves the reader at the top of the changelog, which is a
       * reasonable place to be when the version they asked for has no published notes.
       * @param {number} [attempt]
       */
      openDeepLink(attempt = 0) {
        const hash = (this.$route.hash || '').replace('#', '');
        // A hash names its entry outright; a version has to be matched against the loaded
        // releases, so it is re-resolved after every page rather than resolved once up front.
        const version = hash ? '' : String(this.$route.query.version || '');
        if (!hash && !versionKey(version)) return Promise.resolve();

        const anchorId = hash
          ? (this.releaseByAnchor(hash) && hash) || ''
          : this.anchorForVersion(version);

        if (anchorId) {
          // The entry exists in `releases` but may not be in the DOM yet on the tick it arrived.
          this.$nextTick(() => this.goToVersion(anchorId));
          return Promise.resolve();
        }
        if (attempt >= MAX_DEEP_LINK_PAGES - 1 || !this.hasMore) return Promise.resolve();
        return this.loadMore().then(() => this.openDeepLink(attempt + 1));
      },

      // ----------------------------------------------------------- subscription
      openPreferences() {
        this.preferencesDialog = true;
        this.subscriptionLoading = true;
        this.$store
          .dispatch('RELEASE_NOTES/FETCH_SUBSCRIPTION')
          .then((data) => {
            if (!data) return;
            const key = SUBSCRIBED_KEYS.find((candidate) =>
              Object.prototype.hasOwnProperty.call(data, candidate),
            );
            if (key) this.subscriptionKey = key;
            this.subscribed = !!data[this.subscriptionKey];
          })
          .finally(() => {
            this.subscriptionLoading = false;
          });
      },

      savePreferences() {
        this.subscriptionSaving = true;
        // Echo back whichever boolean spelling the GET returned, so the PUT matches the DTO.
        const payload = { [this.subscriptionKey]: this.subscribed };
        this.$store
          .dispatch('RELEASE_NOTES/UPDATE_SUBSCRIPTION', payload)
          .then(() => {
            this.preferencesDialog = false;
            this.$dialog.notify.success('Email preferences saved.', {
              position: 'top-right',
              timeout: 3000,
            });
          })
          .catch((err) => {
            // The Vuex action has already surfaced the error; keep the dialog open to retry.
            error('Failed to update release notes subscription', err);
          })
          .finally(() => {
            this.subscriptionSaving = false;
          });
      },
    },
  };
</script>

<style scoped lang="scss">
  .whats-new {
    --whats-new-header-height: 73px;
  }

  .whats-new__header {
    position: sticky;
    top: 0;
    z-index: var(--ds-z-sticky);
  }

  .whats-new__header-inner {
    display: flex;
    align-items: center;
    gap: var(--ds-space-md);
    padding: var(--ds-space-md);
  }

  .whats-new__header-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 36px;
    height: 36px;
    border-radius: var(--ds-radius-md);
    background: rgba(44, 194, 149, 0.12);
  }

  .whats-new__header-text {
    flex: 1 1 auto;
    min-width: 0;
  }

  .whats-new__title {
    margin: 0;
    font-family: var(--ds-font-family-heading);
    font-size: var(--ds-font-title);
    font-weight: 600;
    line-height: 1.3;
    color: var(--v-text-base);
  }

  .whats-new__subtitle {
    margin: 0;
    font-size: var(--ds-font-caption);
    line-height: 1.4;
    color: var(--v-text_secondary-base);
  }

  .whats-new__body {
    padding: var(--ds-space-lg) var(--ds-space-md) var(--ds-space-2xl);
  }

  .whats-new__skeleton {
    max-width: 760px;
  }

  .whats-new__layout {
    display: grid;
    grid-template-columns: 232px minmax(0, 1fr);
    gap: var(--ds-space-2xl);
    align-items: start;
  }

  .whats-new__rail {
    // Clears the sticky page header so the rail never slides underneath it.
    --version-rail-top: calc(var(--whats-new-header-height) + var(--ds-space-md));
    --version-rail-max-height: calc(100vh - var(--whats-new-header-height) - 140px);
  }

  .whats-new__stream {
    min-width: 0;
    // Caps the measure of the release bodies; long prose at full width is unreadable.
    max-width: 760px;
  }

  .whats-new__more {
    display: flex;
    justify-content: center;
    padding: var(--ds-space-md) 0 var(--ds-space-2xl);
  }

  .whats-new__end {
    margin: 0;
    padding-left: 48px;
    font-size: var(--ds-font-caption);
    color: var(--v-text_secondary-base);
    opacity: 0.7;
  }

  @media (max-width: 959px) {
    .whats-new__layout {
      grid-template-columns: minmax(0, 1fr);
      gap: 0;
    }

    .whats-new__rail--compact {
      --version-rail-top: var(--whats-new-header-height);

      margin-bottom: var(--ds-space-md);
    }

    .whats-new__stream {
      max-width: none;
    }

    .whats-new__end {
      padding-left: 28px;
    }
  }
</style>
